import {
  AfterViewInit,
  Component,
  ComponentRef,
  ElementRef,
  OnInit,
  ViewChild
} from '@angular/core';
import { filter, forkJoin, Observable, of, Subscription } from 'rxjs';
import { IWidget } from '../../../interfaces/iWidget';
import { CommActionItem } from '../../../interfaces/comm-action-item';
import { CopilotService } from '../../../services/copilot.service';
import { SettingsService } from '../../../services/settings.service';
import { ActionItemUpdate } from '../../../action-item-update';
import { CopilotMeeting } from '../../../interfaces/copilot-meeting';
import { SettingsEventType } from '../../../models/enums/settingsEventType';
import { SyncEventType } from '../../../models/enums/syncEventType';
import { SynchronizationService } from '../../../services/synchronization.service';
import { MeetingAnalysis } from '../../../interfaces/meeting-analyse';
import { MessageService } from '../../../services/message.service';
import { PersistenceEvent } from '../../../models/syncEvents/persistenceEvents/persistenceEvent';
import { NotifierService } from 'angular-notifier';


@Component({
  selector: 'app-copilot-widget',
  templateUrl: './copilot-widget.component.html',
  styleUrls: ['./copilot-widget.component.scss']
  })

export class CopilotWidgetComponent implements IWidget, AfterViewInit, OnInit {
  @ViewChild("copilotContainer") copilotContainer: ElementRef;
  private element: ElementRef;
  public values: Observable<string[]>;
  public transcript: Observable<string[]>;
  public actionItems: CommActionItem[];
  /** list of updates detected on existing issues */
  public actionItemUpdates: ActionItemUpdate[];
  public meetings: CopilotMeeting[];
  public selectedMeeting: CopilotMeeting | null;
  public loading = false;

  public meetingAnalysis: MeetingAnalysis;
  private subscriptions: Subscription[] = [];

  public static widgetName = 'copilot';
  public static readonly componentId = 'CopilotWidgetComponent';

  componentRef: ComponentRef<any>;
  isUndocked: boolean;

  constructor(
    private copilotService: CopilotService,
    private settings: SettingsService,
    private sync: SynchronizationService,
    private messageService: MessageService,
    private notifier: NotifierService,
  ) {
    this.initCopilotAnalysisEventSubscription();
  }

  ngOnInit(): void {
    this.settings.getUpdateObservable()
      .pipe(filter((event) => event === SettingsEventType.PROJECT_CHANGED))
      .subscribe(_ => {
        this.resetComponent();
        this.getMeetings(this.settings.getProjectName());
      });

    this.getMeetings(this.settings.getProjectName());
  }

  private getMeetings(projectKey: string) {
    this.meetings = [];
    this.copilotService.getMeetings(projectKey)
      .subscribe(meetings => this.meetings = meetings);
  }

  createMeetingAnalysis(projectKey: string, copilotSessionId: string): void {
    this.messageService.sendTrace(`create meeting analysis for project ${projectKey}`);
    const sprintId = this.settings.getSprintId(projectKey);
    this.loading = true;
    this.copilotService.createMeetingAnalysis(projectKey, sprintId, copilotSessionId)
      .subscribe(id => {
        this.messageService.sendTrace(`meeting analysis initiated (resource id ${id}) for project ${projectKey}`);

        const event: PersistenceEvent = {
          type: SyncEventType.COPILOT_MEETING_ANALYSIS_ID,
          getPayload: (): CopilotAnalysisIdEventPayload => ({
            projectKey: projectKey,
            analysisId: id,
            copilotSessionId: copilotSessionId,
          }),
        };

        this.sync.sendPersistenceEventNew(
          event,
          false
        );
      });
  }

  ngOnDestroy(): void {
    for (const ev of this.subscriptions) {
      ev.unsubscribe();
    }
  }

  ngAfterViewInit(): void {
    this.element = this.copilotContainer;
  }

  setPosition(x: number, y: number) {
    this.element.nativeElement.style.left = x + 'px';
    this.element.nativeElement.style.top = y + 'px';
  }


  onRemoveActionItem(itemId: number) {
    this.actionItems = this.actionItems.filter((item) => item.id !== itemId);
  }

  onRemoveUpdate(itemId: number) {
    this.actionItemUpdates = this.actionItemUpdates.filter(item => item.id !== itemId);
  }

  onSelectedSessionChanged() {
    if (this.selectedMeeting !== null) {
      this.messageService.sendTrace(`meeting ${this.selectedMeeting.sessionId} selected for analysis`);
      const projectKey = this.settings.getProjectName();
      const sessionId = this.selectedMeeting.sessionId;

      this.createMeetingAnalysis(projectKey, sessionId);

      // TODO add loading indicator
    }
  }

  refresh() {
    this.getMeetings(this.settings.getProjectName());
    this.resetComponent();
  }

  /** Reset all view relevant data such as actionItems, updates, ... */
  private resetComponent() {
    this.selectedMeeting = null;
    this.transcript = of([]); // TODO is this the way??
    this.actionItems = [];
    this.actionItemUpdates = [];
    this.loading = false;
  }

  private initCopilotAnalysisEventSubscription(): void {
    const copilotIdSubscription: Subscription = this.sync
      .subscribeEvent<CopilotAnalysisIdEventPayload>(
      SyncEventType.COPILOT_MEETING_ANALYSIS_ID
    ).subscribe(({ analysisId, projectKey, copilotSessionId }) => {
      this.messageService.sendTrace(`received meeting analysis resource id ${analysisId}`);

      if (projectKey === this.settings.getProjectName()) {
        this.loading = true;
        this.notifier.notify('info', 'Loading meeting analysis'); // TODO figure out, why this alert is sent at some random points too

        forkJoin([
          this.copilotService.getMeetingAnalysis(analysisId),
          this.copilotService.getMeetings(projectKey), // (re) fetch the meetings here to ensure the client actually has a list of meetings
        ]).subscribe(([meetingAnalysis, meetings]) => {
          this.messageService.sendTrace(`meeting analysis received:  ${JSON.stringify(meetingAnalysis)}`);

          this.actionItems = meetingAnalysis.actionItems
            .map((item, index) => ({ ...item, id: index }));
          this.actionItemUpdates = meetingAnalysis.updates;

          this.meetings = meetings;
          this.selectedMeeting = meetings.find(({sessionId}) => sessionId === copilotSessionId);

          this.loading = false;
        });
      }
    });

    this.subscriptions.push(copilotIdSubscription);
  }
}

export interface CopilotAnalysisIdEventPayload {
  projectKey: string; // to allow subscribers to filter if the analysis is relevant for them
  analysisId: string;
  copilotSessionId: string; // to properly update the ui (meeting dropdown list)
}
